/**
 * Reports hub entries — parity with Angular `reports-type.component.html`.
 * `descriptionKey` matches API `report_name` / NgRx store keys for tooltips.
 */

export { angularPathToReactSegment } from '../utils/reportRoutePaths'

import type { ReportHubLink } from '../types'
import { PROPERTY_DETAIL_LIST_URL } from '../types/constants'

export type { ReportHubLink, ReportHubLinkKind } from '../types'
export { PROPERTY_DETAIL_LIST_URL } from '../types/constants'


/**
 * All Reports hub allowlist — only these appear on the hub and stay deeplinkable.
 * Source: approved Report Name list (Active Invites … Chargeback), plus SMS Message Logs.
 * “Mailing Logs” maps to Mailgun Logs.
 */
const ALL_REPORTS_HUB_ALLOWLIST_REACT_PATHS = new Set([
  'applicationInvite',
  'MRG_Report',
  'pmc-performance-red',
  'pmc-performance-green',
  'balances_report',
  'payment-logs',
  'default-payment-logs',
  'mailgun-logs',
  'mail-logs',
  'message-logs',
  'pending-defaults',
  'chargeback',
])

const ALL_REPORTS_HUB_ALLOWLIST_DASHBOARD_PATHS = new Set(['/mailtrap'])

function applyAllReportsHubVisibility(link: ReportHubLink): ReportHubLink {
  let allowed = false
  if (link.kind === 'react' && link.reactPath) {
    allowed = ALL_REPORTS_HUB_ALLOWLIST_REACT_PATHS.has(link.reactPath)
  } else if (link.kind === 'dashboard' && link.dashboardPath) {
    allowed = ALL_REPORTS_HUB_ALLOWLIST_DASHBOARD_PATHS.has(link.dashboardPath)
  }
  return { ...link, hidden: !allowed }
}

/** All hub links in display order (Angular list order). */
const RAW_REPORT_HUB_LINKS: ReportHubLink[] = [
  {
    label: 'Total Number Of Contracts',
    policies: ['reports_total_number_of_contract'],
    kind: 'react',
    reactPath: 'total-number-of-contract',
    descriptionKey: 'reports_total_number_of_contract',
    category: 'performance-growth',
  },
  {
    label: 'Active Invites Status',
    policies: ['reports_invite'],
    kind: 'react',
    reactPath: 'applicationInvite',
    descriptionKey: 'reports_invite',
    requiresLibertyOrg: true,
    category: 'property-applications',
  },
  {
    label: 'API Logs',
    policies: ['reports_api_logs'],
    kind: 'react',
    reactPath: 'api-logs',
    descriptionKey: 'reports_api_logs',
    category: 'system-audit-logs',
  },
  {
    label: 'MRG Report',
    policies: ['reports_insurance_mrg'],
    kind: 'react',
    reactPath: 'MRG_Report',
    descriptionKey: 'reports_insurance_mrg',
    category: 'financial-payments',
  },
  {
    label: 'Weekly Reimbursements Report',
    policies: ['reports_weekly_claims'],
    kind: 'react',
    reactPath: 'financial-mrg',
    descriptionKey: 'reports_weekly_claims',
    category: 'reimbursements',
  },
  {
    label: 'PMC Performance Report - Red',
    policies: ['reports_pmc_performance'],
    kind: 'react',
    reactPath: 'pmc-performance-red',
    descriptionKey: 'reports_pmc_performance',
    category: 'performance-growth',
  },
  {
    label: 'PMC Performance Report- Green',
    policies: ['reports_pmc_performance'],
    kind: 'react',
    reactPath: 'pmc-performance-green',
    descriptionKey: 'reports_pmc_performance',
    category: 'performance-growth',
  },
  {
    label: 'Balance Report (Outstanding/Adjustment)',
    policies: ['reports_outstanding_balances'],
    kind: 'react',
    reactPath: 'balances_report',
    descriptionKey: 'reports_outstanding_balances',
    category: 'financial-payments',
  },
  {
    label: 'Revenue Payment Logs',
    policies: ['reports_revenue_payment_logs'],
    kind: 'react',
    reactPath: 'payment-logs',
    descriptionKey: 'reports_revenue_payment_logs',
    category: 'financial-payments',
  },
  {
    label: 'Default Payment Logs',
    policies: ['reports_default_payment_logs'],
    kind: 'react',
    reactPath: 'default-payment-logs',
    descriptionKey: 'reports_default_payment_logs',
    category: 'financial-payments',
  },
  {
    label: 'SMS Message Logs',
    policies: ['reports_message_logs'],
    kind: 'react',
    reactPath: 'message-logs',
    descriptionKey: 'reports_message_logs',
    category: 'communication',
  },
  {
    label: 'Impersonate Logs',
    policies: ['reports_impersonate_logs'],
    kind: 'react',
    reactPath: 'impersonate-logs',
    descriptionKey: 'reports_impersonate_logs',
    category: 'system-audit-logs',
  },
  {
    label: 'Mailgun Logs',
    policies: ['reports_mailgun_logs'],
    kind: 'react',
    reactPath: 'mailgun-logs',
    descriptionKey: 'reports_mailgun_logs',
    category: 'communication',
  },
  {
    label: 'Mail Logs (legacy)',
    policies: ['reports_mailgun_logs'],
    kind: 'react',
    reactPath: 'mail-logs',
    descriptionKey: 'reports_mailgun_logs',
    category: 'communication',
  },
  {
    label: 'Mailtrap Logs',
    policies: ['reports_mailtrap_logs'],
    kind: 'dashboard',
    dashboardPath: '/mailtrap',
    descriptionKey: 'reports_mailtrap_logs',
    hideOnHostnames: ['cornerstone.libertyrent.com'],
    category: 'communication',
  },
  {
    label: 'Financial Daily Report',
    policies: ['reports_financial_daily_payment'],
    kind: 'react',
    reactPath: 'financial-daily',
    descriptionKey: 'reports_financial_daily_payment',
    category: 'financial-payments',
  },
  {
    label: 'Financial Daily Report - Defaults Paid',
    policies: ['reports_financial_daily_default'],
    kind: 'react',
    reactPath: 'financial-daily-defaults',
    descriptionKey: 'reports_financial_daily_default',
    category: 'financial-payments',
  },
  {
    label: 'Non-verified Property Report',
    policies: ['reports_nonverified_property'],
    kind: 'react',
    reactPath: 'non-verified-property',
    descriptionKey: 'reports_nonverified_property',
    category: 'performance-growth',
  },
  {
    label: 'Pending Reimbursements Report',
    policies: ['reports_pending_defaults'],
    kind: 'react',
    reactPath: 'pending-defaults',
    descriptionKey: 'reports_pending_defaults',
    category: 'reimbursements',
  },
  {
    label: 'Reimbursement Not Opened Timely',
    policies: ['reports_claim_not_timely'],
    kind: 'react',
    reactPath: 'claim-not-timely',
    descriptionKey: 'reports_claim_not_timely',
    category: 'reimbursements',
  },
  {
    label: 'Auto Expire Reimbursements Report',
    policies: ['reports_expire_defaults'],
    kind: 'react',
    reactPath: 'expire-defaults',
    descriptionKey: 'reports_auto_expire_claims',
    category: 'reimbursements',
  },
  {
    label: 'Active Properties No Application Report',
    policies: ['reports_active_properties_no_application'],
    kind: 'react',
    reactPath: 'active-properties-no-application',
    descriptionKey: 'reports_active_properties_no_application',
    category: 'property-applications',
  },
  {
    label: 'Property Detail List',
    policies: ['reports_property_detail'],
    kind: 'external',
    externalHref: PROPERTY_DETAIL_LIST_URL,
    descriptionKey: 'reports_property_detail',
    category: 'property-applications',
  },
  {
    label: 'Chargeback Report',
    policies: ['reports_chargeback'],
    kind: 'react',
    reactPath: 'chargeback',
    descriptionKey: 'reports_chargeback',
    category: 'financial-payments',
  },
  {
    label: 'Missing Contacts Report',
    policies: ['reports_missing_contacts'],
    kind: 'react',
    reactPath: 'missing-contacts',
    descriptionKey: 'reports_missing_contacts',
    category: 'property-applications',
  },
  {
    label: 'IMAP Inbox',
    policies: ['reports_imap_inbox'],
    kind: 'dashboard',
    dashboardPath: '/imap-logs',
    descriptionKey: 'reports_imap_inbox',
    category: 'communication',
  },
  {
    label: 'AI Report',
    policies: ['reports_ai'],
    kind: 'external',
    descriptionKey: 'reports_ai',
    category: 'system-audit-logs',
  },
  {
    label: 'Mobile Event Log',
    policies: ['reports_mobile_event_log'],
    kind: 'react',
    reactPath: 'mobile-event-log',
    descriptionKey: 'reports_mobile_event_log',
    category: 'system-audit-logs',
  },
  {
    label: 'Failed Logins Log',
    policies: ['reports_failed_logins'],
    kind: 'react',
    reactPath: 'failed-logins',
    descriptionKey: 'reports_failed_logins',
    category: 'system-audit-logs',
  },
  {
    label: 'Daily Application Report',
    policies: ['reports_daily_application'],
    kind: 'react',
    reactPath: 'daily-application',
    descriptionKey: 'reports_daily_application',
    category: 'property-applications',
  },
  {
    label: 'Contract Loss Ratio',
    policies: ['reports_contract_loss_ratio'],
    kind: 'react',
    reactPath: 'contract-loss-ratio',
    descriptionKey: 'reports_contract_loss_ratio',
    category: 'performance-growth',
  },
  {
    label: 'MRG Loss Ratio',
    policies: ['reports_mrg_loss_ratio'],
    kind: 'react',
    reactPath: 'mrg-loss-ratio',
    descriptionKey: 'reports_mrg_loss_ratio',
    category: 'performance-growth',
  },
  {
    label: 'W9 Uploaded Report',
    policies: ['reports_w9_uploaded'],
    kind: 'react',
    reactPath: 'w9-uploaded',
    descriptionKey: 'reports_w9_uploaded',
    category: 'property-applications',
  },
  {
    label: 'Organic Growth',
    policies: ['reports_organic_growth'],
    kind: 'react',
    reactPath: 'organic-growth',
    descriptionKey: 'reports_organic_growth',
    category: 'performance-growth',
  },
  {
    label: 'Approved Not Purchased',
    policies: ['reports_approved_not_purchased'],
    kind: 'react',
    reactPath: 'approved-not-purchased',
    descriptionKey: 'reports_approved_not_purchased',
    category: 'property-applications',
  },
  {
    label: 'Application Completed Fee Not Paid',
    policies: ['reports_application_completed_fee_not_paid'],
    kind: 'react',
    reactPath: 'application-completed-fee-not-paid',
    descriptionKey: 'reports_application_completed_fee_not_paid',
    category: 'reimbursements',
  },
  {
    label: 'Property Activity',
    policies: ['reports_property_activity'],
    kind: 'react',
    reactPath: 'property-activity',
    descriptionKey: 'reports_property_activity',
    category: 'property-applications',
  },
  {
    label: 'LR Form 16',
    policies: ['reports_lr_form_16'],
    kind: 'react',
    reactPath: 'lr-form-16',
    descriptionKey: 'reports_lr_form_16',
    category: 'system-audit-logs',
  },
  {
    label: 'Dynamic Pricing',
    policies: ['reports_dynamic_pricing'],
    kind: 'react',
    reactPath: 'dynamic-pricing',
    descriptionKey: 'dynamic_pricing',
    category: 'financial-payments',
  },
]

/**
 * Hub + route source of truth. Hidden entries stay in this list for parity/
 * tests but are omitted from the UI and React route registration.
 */
export const REPORT_HUB_LINKS: ReportHubLink[] = RAW_REPORT_HUB_LINKS.map(
  applyAllReportsHubVisibility,
)

/** Links shown on All Reports (and eligible for auto route registration). */
export const VISIBLE_REPORT_HUB_LINKS: ReportHubLink[] = REPORT_HUB_LINKS.filter(
  (link) => !link.hidden,
)

/** React path segments that must not be deeplinkable. */
export const HIDDEN_REPORT_REACT_PATHS: ReadonlySet<string> = new Set(
  REPORT_HUB_LINKS.filter((link) => link.hidden && link.kind === 'react' && link.reactPath).map(
    (link) => link.reactPath as string,
  ),
)

export function isReportHubLinkHidden(link: ReportHubLink): boolean {
  return Boolean(link.hidden)
}
